<script setup lang="ts">
import {
  $uploadGlobalProjectFiles,
  $uploadProjectFiles,
  $uploadSelectedItemProjectFiles,
  getFileManagementFolders,
  type ProjectFileUploadResultItem,
  type ProjectFilesUploadResponse,
  type UploadStatusWarning,
} from '@/api/fileManagement'
import { getFileIcon, getFileIconColor } from '@/helpers/utils/files/getFileIcon'
import { useSnackbarStore } from '@/store/snackbar'
import { useResizableDialog } from '@/helpers/composables/useResizableDialog'

type UploadExpectedItem = {
  id: number | string
  name: string
  statusName?: string | null
}

type UploadExpectedDisplayItem = UploadExpectedItem & {
  displayName: string
}

type UploadCampaign = {
  id: number
  name?: string
  title?: string
}

interface Props {
  modelValue: boolean
  projectId: number | string | string[] | null | undefined
  selectedItemIds?: number[] | null
  expectedItems?: UploadExpectedItem[] | null
  workOrderId?: number | string | string[] | null | undefined
  projectTypeId: number // wf: 1, branding: 2, kop: 5, retail: 0
  isGlobalUpload?: boolean
  waitingRoomProjectOptions?: UploadCampaign[] | null
}

const fileNameDisclaimer: Record<number, Record<string, string>> = {
  // WF
  1: {
    ARTWORK: "The file name must match the item's Code exactly. No extra characters or spaces.",
    INSTALLATION: "The file name must start with the Code, and can be followed by a space and any additional text.",
    SCOPING_PHOTOS: "The file name must start with the Code, and can be followed by a space and any additional text.",
    PREPRESS_PROOF: "The file name must match the item's Code exactly. No extra characters or spaces.",
    SCOPE: "The file name must match the item's Code exactly. No extra characters or spaces.",
  },
  // Branding
  2: {
    ARTWORK: "The file name must match the system's Filename exactly. No extra characters or spaces.",
    INSTALLATION: "The file name must start with the exact Filename, and can be followed by a space and any additional text.",
    SCOPING_PHOTOS: "The file name must start with the exact Filename, and can be followed by a space and any additional text.",
    PREPRESS_PROOF: "The file name must match the system's Filename exactly. No extra characters or spaces.",
    SCOPE: "The file name must match the system's Filename exactly. No extra characters or spaces.",
  },
  // KOP
  5: {
    ARTWORK: "The file name must match the item's Code exactly. No extra characters or spaces.",
    INSTALLATION: "The file name must start with the Code, and can be followed by a space and any additional text.",
    SCOPING_PHOTOS: "The file name must start with the Code, and can be followed by a space and any additional text.",
    PREPRESS_PROOF: "The file name must match the item's Code exactly. No extra characters or spaces.",
    SCOPE: "The file name must match the item's Code exactly. No extra characters or spaces.",
  },

}
const props = withDefaults(defineProps<Props>(), {
  selectedItemIds: null,
  expectedItems: null,
  isGlobalUpload: false,
  waitingRoomProjectOptions: null,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'uploaded'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const {
  dialogWidth,
  dialogHeight,
  dialogMinWidth,
  dialogMinHeight,
} = useResizableDialog({
  defaultWidth: 1100,
  defaultHeight: 760,
  minWidth: 720,
  minHeight: 520,
})

const folder = ref<string | null>(null)
const selectedFiles = ref<File[]>([])
const selectedUploadCampaigns = ref<UploadCampaign[]>([])
const isUploading = ref<boolean>(false)
const isUploadingAnyway = ref<boolean>(false)
const isReuploading = ref<boolean>(false)
const isFilesUploadDragActive = ref<boolean>(false)
const showWaitingRoomProjectDialog = ref(false)
const pendingWaitingRoomIssueIds = ref<string[]>([])
const selectedWaitingRoomCampaign = ref<UploadCampaign | null>(null)
const selectedFilenameIssueIds = ref<string[]>([])
const showReuploadDialog = ref(false)
const reuploadIssue = ref<UploadFilenameIssue | null>(null)
const reuploadSelectedFiles = ref<File[]>([])
const reuploadErrorMessage = ref('')

type UploadResultGroup = {
  kind?: 'filename-issues'
  title: string
  color: string
  icon: string
  countLabel?: string
  infoMessage?: string
  files: Array<{
    key?: string
    name: string
    message?: string
    link?: string
    multilineMessage?: boolean
  }>
}

type UploadBackendError = {
  id: string
  file: string
  message: string
}

type UploadFilenameIssue = {
  id: string
  file: File
  originalName: string
  message: string
}

type UploadAttemptResult = {
  successfulFiles: File[]
  successCount: number
  filenameIssueCount: number
  statusWarningCount: number
  errorCount: number
}

type UploadSnackbar = {
  id: string
  color: 'success' | 'warning' | 'error'
  icon: string
  title: string
  text: string
  timeout: number
  show: boolean
}

const uploadedFileNames = ref<string[]>([])
const replacedFileNames = ref<string[]>([])
const waitingRoomFileNames = ref<string[]>([])
const uploadFailures = ref<UploadBackendError[]>([])
const filenameIssues = ref<UploadFilenameIssue[]>([])
const uploadStatusWarnings = ref<UploadStatusWarning[]>([])
const uploadSnackbars = ref<UploadSnackbar[]>([])

const normalizedFiles = computed<File[]>(() => selectedFiles.value)
const uploadFolderOrder = ['SCOPE', 'ARTWORK', 'PREPRESS_PROOF', 'SCOPING_PHOTOS', 'INSTALLATION']
const uploadFolderTitles: Record<string, string> = {
  SCOPE: 'Scope',
  ARTWORK: 'Artwork',
  PREPRESS_PROOF: 'Prepress Proof',
  SCOPING_PHOTOS: 'Scoping Photo',
  INSTALLATION: 'Installation Photo',
}
const availableFolders = computed(() => {
  const allowedFolders = getFileManagementFolders(props.projectTypeId)

  return uploadFolderOrder.flatMap(value => {
    const option = allowedFolders.find(folderOption => folderOption.value === value)
    return option ? [{ ...option, title: uploadFolderTitles[value] }] : []
  })
})

const getExpectedItemDisplayName = (item: UploadExpectedItem) => {
  const name = String(item.name || '').trim() || `Item #${item.id}`
  const statusName = String(item.statusName || '').trim() || 'No status'

  return `${name} - ${statusName}`
}

const sortedExpectedItems = computed<UploadExpectedDisplayItem[]>(() => {
  const items = props.expectedItems || []

  return items
    .map(item => ({
      ...item,
      displayName: getExpectedItemDisplayName(item),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }))
})

const sortedSelectedUploadFiles = computed<File[]>(() => {
  return [...normalizedFiles.value]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
})

const sortedSelectedUploadFileNames = computed<string[]>(() => {
  return sortedSelectedUploadFiles.value.map(file => file.name)
})

const showSelectionComparison = computed(() => {
  return !props.isGlobalUpload && sortedExpectedItems.value.length > 0
})

const showSelectedUploadFilesPanel = computed(() => {
  return showSelectionComparison.value || (props.isGlobalUpload && sortedSelectedUploadFiles.value.length > 0)
})

const selectedUploadCampaignIds = computed<number[]>(() => {
  return selectedUploadCampaigns.value
    .map(campaign => Number(campaign.id))
    .filter(campaignId => !Number.isNaN(campaignId))
})

const waitingRoomCampaignOptions = computed<UploadCampaign[]>(() => {
  if (props.isGlobalUpload && selectedUploadCampaigns.value.length) {
    return selectedUploadCampaigns.value
  }

  if (props.waitingRoomProjectOptions?.length) {
    return props.waitingRoomProjectOptions
  }

  const projectId = Number(resolvedProjectId.value)
  if (!Number.isNaN(projectId) && projectId > 0) {
    return [{ id: projectId, name: `Project #${projectId}` }]
  }

  return []
})

const activeFileNameDisclaimer = computed(() => {
  if (!folder.value) return 'Folder should be selected for files upload'
  return fileNameDisclaimer[props.projectTypeId]?.[folder.value] || ''
})

const hasSelectedItems = computed(() => !!props.selectedItemIds?.length)

const resolvedProjectId = computed<number | string | null>(() => {
  if (Array.isArray(props.projectId)) {
    return props.projectId[0] ?? null
  }

  return props.projectId ?? null
})

const resolvedWorkOrderId = computed<number | string | null>(() => {
  if (Array.isArray(props.workOrderId)) {
    return props.workOrderId[0] ?? null
  }

  return props.workOrderId ?? null
})

const isReadyToUpload = computed(() => {
  if (hasSelectedItems.value) {
    return !!folder.value && !!normalizedFiles.value.length
  }

  if (props.isGlobalUpload) {
    return !!folder.value
      && !!normalizedFiles.value.length
      && selectedUploadCampaignIds.value.length > 0
  }

  return !!resolvedProjectId.value && !!folder.value && !!normalizedFiles.value.length
})

const isUploadActionBusy = computed(() => {
  return isUploading.value || isUploadingAnyway.value || isReuploading.value
})

const selectedFilenameIssues = computed(() => {
  const selectedIds = new Set(selectedFilenameIssueIds.value)
  return filenameIssues.value.filter(issue => selectedIds.has(issue.id))
})

const areAllFilenameIssuesSelected = computed(() => {
  return !!filenameIssues.value.length && selectedFilenameIssueIds.value.length === filenameIssues.value.length
})

const isFilenameIssueSelectionIndeterminate = computed(() => {
  return !!selectedFilenameIssueIds.value.length && !areAllFilenameIssuesSelected.value
})

const hasFilesUploadResults = computed(() => {
  return !!uploadedFileNames.value.length
    || !!replacedFileNames.value.length
    || !!waitingRoomFileNames.value.length
    || !!uploadFailures.value.length
    || !!filenameIssues.value.length
    || !!uploadStatusWarnings.value.length
})

const filesUploadResultGroups = computed<UploadResultGroup[]>(() => {
  const groups: UploadResultGroup[] = []
  const successfulFileNames = [
    ...uploadedFileNames.value,
    ...replacedFileNames.value,
    ...waitingRoomFileNames.value,
  ]

  if (successfulFileNames.length) {
    groups.push({
      title: 'Files uploaded successfully',
      color: 'success',
      icon: 'tabler-circle-check',
      infoMessage: [
        `${uploadedFileNames.value.length + replacedFileNames.value.length} file(s) uploaded/replaced into Item Files.`,
        `${waitingRoomFileNames.value.length} file(s) uploaded to Waiting Room.`,
        uploadFailures.value.length ? `${uploadFailures.value.length} file(s) not uploaded due to upload errors.` : '',
      ].filter(Boolean).join('\n'),
      files: successfulFileNames.map(name => ({ name })),
    })
  }

  if (uploadStatusWarnings.value.length) {
    groups.push({
      title: 'Files uploaded, statuses not changed',
      color: 'warning',
      icon: 'tabler-alert-triangle',
      countLabel: 'items',
      files: uploadStatusWarnings.value.map((warning, index) => {
        const itemType = warning.item_type === 'line_item' ? 'Line item' : 'Sign'
        const currentStatus = warning.current_status || 'No status'

        return {
          key: `${warning.filename}-${warning.item_type}-${warning.item_id}-${index}`,
          name: `${itemType} ${warning.item_display_name} - file ${warning.filename}`,
          message: `${warning.message}\nCurrent status: ${currentStatus}`,
          link: warning.link,
          multilineMessage: true,
        }
      }),
    })
  }

  if (filenameIssues.value.length) {
    groups.push({
      kind: 'filename-issues',
      title: 'Filename review needed',
      color: 'warning',
      icon: 'tabler-alert-triangle',
      files: [],
    })
  }

  if (uploadFailures.value.length) {
    groups.push({
      title: 'Files not uploaded',
      color: 'error',
      icon: 'tabler-circle-x',
      files: uploadFailures.value.map(failure => ({
        key: failure.id,
        name: failure.file,
        message: failure.message,
      })),
    })
  }

  return groups
})

const resetState = () => {
  uploadSnackbars.value = []
  folder.value = null
  selectedFiles.value = []
  selectedUploadCampaigns.value = []
  isUploading.value = false
  isUploadingAnyway.value = false
  isReuploading.value = false
  isFilesUploadDragActive.value = false
  showWaitingRoomProjectDialog.value = false
  pendingWaitingRoomIssueIds.value = []
  selectedWaitingRoomCampaign.value = null
  selectedFilenameIssueIds.value = []
  showReuploadDialog.value = false
  reuploadIssue.value = null
  reuploadSelectedFiles.value = []
  reuploadErrorMessage.value = ''
  uploadedFileNames.value = []
  replacedFileNames.value = []
  waitingRoomFileNames.value = []
  uploadFailures.value = []
  filenameIssues.value = []
  uploadStatusWarnings.value = []
}

watch(() => props.modelValue, () => {
  if (!props.modelValue) {
    resetState()
  }
})

watch(
  availableFolders,
  folders => {
    if (folder.value && !folders.some(item => item.value === folder.value)) {
      folder.value = null
    }
  },
  { immediate: true },
)

watch(filenameIssues, issues => {
  const availableIds = new Set(issues.map(issue => issue.id))
  selectedFilenameIssueIds.value = selectedFilenameIssueIds.value.filter(id => availableIds.has(id))
})

const closeDialog = () => {
  emit('update:modelValue', false)
}

const onDialogModelValueUpdate = (nextModelValue: boolean) => {
  emit('update:modelValue', nextModelValue)
}

const getFileNameList = () => {
  return normalizedFiles.value.map(file => file.name)
}

const getFileIdentity = (file: File) => `${file.name}::${file.size}::${file.lastModified}`

const normalizeIncomingFiles = (files: File[] | File | null | undefined): File[] => {
  return Array.isArray(files) ? files : files ? [files] : []
}

const isSubsetByIdentity = (subset: File[], superset: File[]) => {
  const supersetIds = new Set(superset.map(getFileIdentity))
  return subset.every(file => supersetIds.has(getFileIdentity(file)))
}

const mergeFilesDedup = (current: File[], incoming: File[]) => {
  const merged = [...current, ...incoming]
  const seen = new Set<string>()

  return merged.filter(file => {
    const identity = getFileIdentity(file)
    if (seen.has(identity)) {
      return false
    }

    seen.add(identity)
    return true
  })
}

const onFilesUploadChange = (files: File[] | File | null | undefined) => {
  if (isUploadActionBusy.value) return

  const incomingFiles = normalizeIncomingFiles(files)

  // Chip remove/clear operations send the remaining files.
  if (incomingFiles.length <= selectedFiles.value.length && isSubsetByIdentity(incomingFiles, selectedFiles.value)) {
    selectedFiles.value = incomingFiles
    return
  }

  // File picker selections are additive; keep existing files and append new unique ones.
  selectedFiles.value = mergeFilesDedup(selectedFiles.value, incomingFiles)
}

const onFilesUploadDrop = (event: DragEvent) => {
  isFilesUploadDragActive.value = false
  if (isUploadActionBusy.value) return

  const droppedFiles = Array.from(event.dataTransfer?.files || [])

  if (!droppedFiles.length) {
    return
  }

  selectedFiles.value = mergeFilesDedup(selectedFiles.value, droppedFiles)
}

const removeSelectedFiles = (filesToRemove: File[]) => {
  const identitiesToRemove = new Set(filesToRemove.map(getFileIdentity))
  selectedFiles.value = selectedFiles.value.filter(file => !identitiesToRemove.has(getFileIdentity(file)))
}

const normalizeErrorValue = (input: unknown): string => {
  if (typeof input === 'string') {
    return input
  }

  if (Array.isArray(input)) {
    return input.map(item => normalizeErrorValue(item)).filter(Boolean).join(', ')
  }

  if (input && typeof input === 'object') {
    return Object.values(input as Record<string, unknown>)
      .map(item => normalizeErrorValue(item))
      .filter(Boolean)
      .join(', ')
  }

  return ''
}

const getBackendErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail
  if (detail !== undefined) {
    const detailMessage = normalizeErrorValue(detail)
    if (detailMessage) {
      return detailMessage
    }
  }

  const message = error?.response?.data?.message
  if (message !== undefined) {
    const messageText = normalizeErrorValue(message)
    if (messageText) {
      return messageText
    }
  }

  return error?.message || 'Upload failed'
}

const buildUploadFormData = (
  files: File[],
  uploadFolder: string,
  deferWaitingRoomUpload: boolean,
  waitingRoomCampaignId?: number | null,
) => {
  const formData = new FormData()
  formData.append('folder', uploadFolder)
  formData.append('defer_waiting_room_upload', deferWaitingRoomUpload ? 'true' : 'false')
  for (const file of files) {
    formData.append('files', file)
  }
  if (props.selectedItemIds && props.selectedItemIds.length) {
    formData.append('selected_item_ids', JSON.stringify(props.selectedItemIds))
  }
  if (resolvedWorkOrderId.value) {
    formData.append('work_order_id', String(resolvedWorkOrderId.value))
  }
  if (hasSelectedItems.value || props.isGlobalUpload) {
    formData.append('campaign_type', String(props.projectTypeId))
  }
  if (props.isGlobalUpload && !hasSelectedItems.value && selectedUploadCampaignIds.value.length) {
    formData.append('selected_campaign_ids', JSON.stringify(selectedUploadCampaignIds.value))
  }
  if (waitingRoomCampaignId) {
    formData.append('waiting_room_campaign_id', String(waitingRoomCampaignId))
  }

  return formData
}

const submitUploadFormData = async (formData: FormData) => {
  return hasSelectedItems.value
    ? await $uploadSelectedItemProjectFiles(formData)
    : props.isGlobalUpload
      ? await $uploadGlobalProjectFiles(formData)
      : await $uploadProjectFiles(resolvedProjectId.value as number | string, formData)
}

const findIssueSourceFile = (
  issueFile: ProjectFileUploadResultItem,
  sourceFiles: File[],
  usedIndexes: Set<number>,
) => {
  const issueName = issueFile.filename
  const index = sourceFiles.findIndex((file, fileIndex) => file.name === issueName && !usedIndexes.has(fileIndex))
  if (index >= 0) {
    usedIndexes.add(index)
    return sourceFiles[index]
  }

  return sourceFiles.find(file => file.name === issueName) || null
}

const getUploadPayloadItems = (payload: ProjectFilesUploadResponse) => {
  return {
    uploadedFiles: payload.uploaded_files || [],
    replacedFiles: payload.replaced_files || [],
    notUploadedFiles: payload.not_uploaded_files || [],
    waitingRoomFiles: payload.waiting_room_files || [],
    filenameIssueFiles: payload.filename_issue_files || [],
    statusWarnings: payload.status_warnings || [],
  }
}

const buildUploadAttemptResult = (
  payload: ProjectFilesUploadResponse,
  sourceFiles: File[],
): UploadAttemptResult => {
  const {
    uploadedFiles,
    replacedFiles,
    notUploadedFiles,
    waitingRoomFiles,
    filenameIssueFiles,
  } = getUploadPayloadItems(payload)
  const unsuccessfulNameCounts = new Map<string, number>()

  for (const item of [...notUploadedFiles, ...filenameIssueFiles]) {
    unsuccessfulNameCounts.set(item.filename, (unsuccessfulNameCounts.get(item.filename) || 0) + 1)
  }

  const successfulCandidates: File[] = []
  for (const file of sourceFiles) {
    const remainingUnsuccessfulCount = unsuccessfulNameCounts.get(file.name) || 0
    if (remainingUnsuccessfulCount) {
      unsuccessfulNameCounts.set(file.name, remainingUnsuccessfulCount - 1)
    } else {
      successfulCandidates.push(file)
    }
  }

  const successCount = uploadedFiles.length + replacedFiles.length + waitingRoomFiles.length
  const successfulFiles = successfulCandidates.slice(0, successCount)

  return {
    successfulFiles,
    successCount,
    filenameIssueCount: filenameIssueFiles.length,
    statusWarningCount: payload.status_warnings?.length || 0,
    errorCount: notUploadedFiles.length,
  }
}

const mergeUploadPayload = (
  payload: ProjectFilesUploadResponse,
  sourceFiles: File[],
  options: {
    replaceResults: boolean
    includeFailures?: boolean
    includeFilenameIssues?: boolean
  },
) => {
  const {
    uploadedFiles,
    replacedFiles,
    notUploadedFiles,
    waitingRoomFiles,
    filenameIssueFiles,
    statusWarnings,
  } = getUploadPayloadItems(payload)
  const includeFailures = options.includeFailures !== false
  const includeFilenameIssues = options.includeFilenameIssues !== false

  if (options.replaceResults) {
    uploadedFileNames.value = []
    replacedFileNames.value = []
    waitingRoomFileNames.value = []
    uploadFailures.value = []
    filenameIssues.value = []
    selectedFilenameIssueIds.value = []
    uploadStatusWarnings.value = []
  }

  uploadedFileNames.value.push(...uploadedFiles.map(file => file.filename).filter(Boolean))
  replacedFileNames.value.push(...replacedFiles.map(file => file.filename).filter(Boolean))
  waitingRoomFileNames.value.push(...waitingRoomFiles.map(file => file.filename || file.original_filename).filter(Boolean))
  uploadStatusWarnings.value.push(...statusWarnings)

  if (includeFailures) {
    uploadFailures.value.push(...notUploadedFiles.map((file, index) => ({
      id: `${file.filename || 'file'}-${index}-${Date.now()}`,
      file: file.filename,
      message: file.error || 'Upload failed',
    })))
  }

  if (!includeFilenameIssues) {
    return
  }

  const usedFileIndexes = new Set<number>()
  filenameIssues.value.push(...filenameIssueFiles.map((file, index) => {
    const sourceFile = findIssueSourceFile(file, sourceFiles, usedFileIndexes)
    const filename = file.filename || sourceFile?.name || 'file'

    return {
      id: `${filename}-${index}-${Date.now()}`,
      file: sourceFile || new File([], filename),
      originalName: filename,
      message: file.error || 'Filename needs review',
    }
  }))
}

const showUploadAttemptMessage = (attempt: UploadAttemptResult) => {
  const notifications: UploadSnackbar[] = []

  const addNotification = (notification: Omit<UploadSnackbar, 'id' | 'show' | 'timeout'>) => {
    notifications.push({
      ...notification,
      id: `${notification.color}-${Date.now()}-${notifications.length}`,
      timeout: notification.color === 'error' ? 600000 : 3000,
      show: true,
    })
  }

  if (attempt.errorCount || (!attempt.successCount && !attempt.filenameIssueCount && !attempt.statusWarningCount)) {
    addNotification({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: attempt.errorCount
        ? `${attempt.errorCount} file(s) could not be uploaded.`
        : 'No files were uploaded.',
    })
  }

  const warningCount = attempt.filenameIssueCount + attempt.statusWarningCount
  if (warningCount) {
    const warningDetails = [
      attempt.statusWarningCount ? `${attempt.statusWarningCount} file(s) uploaded, but their statuses were not changed.` : '',
      attempt.filenameIssueCount ? `${attempt.filenameIssueCount} file(s) need filename review.` : '',
    ].filter(Boolean).join(' ')

    addNotification({
      color: 'warning',
      icon: 'mdi-alert',
      title: 'Upload Warning',
      text: warningDetails,
    })
  }

  if (attempt.successCount) {
    const fileLabel = attempt.successCount === 1 ? 'file' : 'files'
    addNotification({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Upload Complete',
      text: `${attempt.successCount} ${fileLabel} uploaded successfully.`,
    })
    emit('uploaded')
  }

  uploadSnackbars.value = notifications
}

const getUploadRequestError = (filesToUpload: File[], uploadFolder: string) => {
  if (!hasSelectedItems.value && !props.isGlobalUpload && !resolvedProjectId.value) {
    return 'Missing project id for this upload.'
  }

  if (!availableFolders.value.some(item => item.value === uploadFolder)) {
    return 'Choose a Folder before uploading.'
  }

  if (!filesToUpload.length) {
    return 'Choose one or more files to upload.'
  }

  return ''
}

const uploadFiles = async () => {
  const filesToUpload = [...normalizedFiles.value]
  const uploadFolder = folder.value ?? ''
  const requestError = getUploadRequestError(filesToUpload, uploadFolder)
  if (requestError) {
    snackbarStore.showMessage({
      color: requestError.startsWith('Missing') ? 'error' : 'warning',
      icon: 'mdi-alert-circle',
      title: requestError.startsWith('Missing') ? 'Upload Failed' : 'No Files Selected',
      text: requestError,
    })
    return
  }

  isUploading.value = true
  uploadSnackbars.value = []

  try {
    const response = await submitUploadFormData(
      buildUploadFormData(filesToUpload, uploadFolder, true),
    )
    const attempt = buildUploadAttemptResult(response.data, filesToUpload)
    mergeUploadPayload(response.data, filesToUpload, { replaceResults: true })
    selectedFiles.value = []
    if (attempt.successCount && !filenameIssues.value.length) {
      folder.value = null
    }
    showUploadAttemptMessage(attempt)
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    isUploading.value = false
  }
}

const removeFilenameIssuesByFiles = (files: File[]) => {
  const successfulIdentities = new Set(files.map(getFileIdentity))
  filenameIssues.value = filenameIssues.value.filter(issue => {
    return !successfulIdentities.has(getFileIdentity(issue.file))
  })
}

const removeResolvedFailures = (files: File[]) => {
  const successfulNames = new Set(files.map(file => file.name))
  uploadFailures.value = uploadFailures.value.filter(failure => !successfulNames.has(failure.file))
}

const updateFilenameIssueMessages = (
  issues: UploadFilenameIssue[],
  payload: ProjectFilesUploadResponse,
) => {
  const errorsByName = new Map<string, string[]>()
  const resultErrors = [
    ...(payload.not_uploaded_files || []),
    ...(payload.filename_issue_files || []),
  ]

  for (const result of resultErrors) {
    const messages = errorsByName.get(result.filename) || []
    messages.push(result.error || 'Upload failed')
    errorsByName.set(result.filename, messages)
  }

  for (const issue of issues) {
    const messages = errorsByName.get(issue.file.name)
    const message = messages?.shift()
    if (message) {
      issue.message = message
    }
  }
}

const toggleAllFilenameIssues = (selected: boolean | null) => {
  selectedFilenameIssueIds.value = selected ? filenameIssues.value.map(issue => issue.id) : []
}

const toggleFilenameIssue = (issueId: string, selected: boolean | null) => {
  if (selected) {
    if (!selectedFilenameIssueIds.value.includes(issueId)) {
      selectedFilenameIssueIds.value.push(issueId)
    }
    return
  }

  selectedFilenameIssueIds.value = selectedFilenameIssueIds.value.filter(id => id !== issueId)
}

const uploadSelectedFilenameIssuesAnyway = async () => {
  const issues = [...selectedFilenameIssues.value]
  if (!issues.length) return

  const campaignOptions = waitingRoomCampaignOptions.value
  if (!campaignOptions.length) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: 'Choose a Project before uploading this file to Waiting Room.',
    })
    return
  }

  if (campaignOptions.length > 1) {
    pendingWaitingRoomIssueIds.value = issues.map(issue => issue.id)
    selectedWaitingRoomCampaign.value = null
    showWaitingRoomProjectDialog.value = true
    return
  }

  await uploadFilenameIssuesToWaitingRoom(issues, campaignOptions[0].id)
}

const uploadFilenameIssuesToWaitingRoom = async (
  issues: UploadFilenameIssue[],
  waitingRoomCampaignId: number,
) => {
  const filesToUpload = issues.map(issue => issue.file)
  const uploadFolder = folder.value ?? ''
  const requestError = getUploadRequestError(filesToUpload, uploadFolder)
  if (requestError) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: requestError,
    })
    return
  }

  isUploadingAnyway.value = true
  try {
    const response = await submitUploadFormData(
      buildUploadFormData(filesToUpload, uploadFolder, false, waitingRoomCampaignId),
    )
    const attempt = buildUploadAttemptResult(response.data, filesToUpload)
    removeResolvedFailures(attempt.successfulFiles)
    mergeUploadPayload(response.data, filesToUpload, {
      replaceResults: false,
      includeFilenameIssues: false,
    })
    removeSelectedFiles(attempt.successfulFiles)
    removeFilenameIssuesByFiles(attempt.successfulFiles)
    updateFilenameIssueMessages(issues, response.data)
    if (attempt.successCount && !filenameIssues.value.length) {
      folder.value = null
    }
    showUploadAttemptMessage(attempt)
  } catch (error) {
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Upload Failed',
      text: getBackendErrorMessage(error),
    })
  } finally {
    isUploadingAnyway.value = false
  }
}

const confirmWaitingRoomProject = async () => {
  const campaignId = selectedWaitingRoomCampaign.value?.id
  if (!campaignId) return

  const pendingIds = new Set(pendingWaitingRoomIssueIds.value)
  const issues = filenameIssues.value.filter(issue => pendingIds.has(issue.id))

  showWaitingRoomProjectDialog.value = false
  pendingWaitingRoomIssueIds.value = []
  selectedWaitingRoomCampaign.value = null
  await uploadFilenameIssuesToWaitingRoom(issues, campaignId)
}

const openReuploadDialog = (issue: UploadFilenameIssue) => {
  reuploadIssue.value = issue
  reuploadSelectedFiles.value = []
  reuploadErrorMessage.value = ''
  showReuploadDialog.value = true
}

const resetReuploadDialog = () => {
  showReuploadDialog.value = false
  reuploadIssue.value = null
  reuploadSelectedFiles.value = []
  reuploadErrorMessage.value = ''
}

const onReuploadDialogModelValueUpdate = (value: boolean) => {
  if (value) {
    showReuploadDialog.value = true
    return
  }

  if (!isReuploading.value) {
    resetReuploadDialog()
  }
}

const reuploadFilenameIssue = async () => {
  const issue = reuploadIssue.value
  const replacementFile = reuploadSelectedFiles.value[0]
  if (!issue || !replacementFile) return

  const uploadFolder = folder.value ?? ''
  const requestError = getUploadRequestError([replacementFile], uploadFolder)
  if (requestError) {
    reuploadErrorMessage.value = requestError
    reuploadSelectedFiles.value = []
    return
  }

  isReuploading.value = true
  reuploadErrorMessage.value = ''

  try {
    const response = await submitUploadFormData(
      buildUploadFormData([replacementFile], uploadFolder, true),
    )
    const attempt = buildUploadAttemptResult(response.data, [replacementFile])

    if (!attempt.successCount) {
      const filenameIssueError = response.data.filename_issue_files?.[0]?.error
      const uploadError = response.data.not_uploaded_files?.[0]?.error
      reuploadErrorMessage.value = filenameIssueError || uploadError || 'The replacement file could not be uploaded.'
      reuploadSelectedFiles.value = []
      return
    }

    mergeUploadPayload(response.data, [replacementFile], {
      replaceResults: false,
      includeFailures: false,
      includeFilenameIssues: false,
    })
    removeSelectedFiles([issue.file])
    removeFilenameIssuesByFiles([issue.file])
    removeResolvedFailures([issue.file])
    if (!filenameIssues.value.length) {
      folder.value = null
    }
    showUploadAttemptMessage(attempt)
    resetReuploadDialog()
  } catch (error) {
    reuploadErrorMessage.value = getBackendErrorMessage(error)
    reuploadSelectedFiles.value = []
  } finally {
    isReuploading.value = false
  }
}

</script>

<template>
  <VDialog :model-value="modelValue" :persistent="isUploadActionBusy" max-width="95vw" max-height="95vh"
    :width="dialogWidth" :height="dialogHeight" class="files-upload-resizable-dialog"
    @update:model-value="onDialogModelValueUpdate">
    <DialogResizeHandle v-model:dialogWidth="dialogWidth" v-model:dialogHeight="dialogHeight"
      :min-width="dialogMinWidth" :min-height="dialogMinHeight" />

    <VCard class="files-upload-dialog-card d-flex flex-column h-100">
      <VCardTitle class="vss-movable movable-upload-dialog">
        <MovableDialog />
        <div class="d-flex align-center justify-space-between">
          <span>Files Upload</span>
          <VBtn icon="mdi-close" variant="text" size="small" :disabled="isUploadActionBusy" @click="closeDialog" />
        </div>
      </VCardTitle>

      <VCardText class="files-upload-dialog-content">
        <div class="files-upload-dropzone" :class="{ 'files-upload-dropzone--active': isFilesUploadDragActive }"
          @dragenter.prevent="isFilesUploadDragActive = true" @dragover.prevent="isFilesUploadDragActive = true"
          @dragleave.prevent="isFilesUploadDragActive = false" @drop.prevent="onFilesUploadDrop">
          <VIcon icon="tabler-cloud-upload" size="38" class="mb-2" />
          <div class="text-body-2 font-weight-medium">
            Drop files here
          </div>
          <div class="text-caption text-medium-emphasis">
            or select files below
          </div>
        </div>

        <VRow class="mt-4">
          <VCol cols="12" md="4" class="d-flex align-center gap-3">
            <VIcon icon="tabler-folder" size="22" class="mr-1" color='rgba(95, 73, 139, 1)' />
            <VSelect v-model="folder" :items="availableFolders" item-title="title" item-value="value" label="Folder *"
              className="file-folder-select" :disabled="isUploadActionBusy" />
          </VCol>
          <VCol cols="12" md="8" class="d-flex align-center">
            <VIcon icon="tabler-info-circle" size="24" class="mr-3 ml-3" color='rgba(95, 73, 139, 1)' />
            <span>
              {{ activeFileNameDisclaimer }}
            </span>
          </VCol>

          <VCol v-if="isGlobalUpload" cols="12">
            <div class="files-upload-campaign-select-row d-flex">
              <div class="mr-2">
                <VIcon icon="tabler-list-details" color="rgba(95, 73, 139, 1)" />
              </div>
              <FMMultiCompaignSelect v-model="selectedUploadCampaigns" label="Projects *" :type="projectTypeId"
                :is-select-all-visible="true" :disabled="isUploadActionBusy" />
            </div>
          </VCol>

          <VCol cols="12">
            <VFileInput :model-value="selectedFiles" multiple chips counter show-size label="Select files"
              prepend-icon="tabler-paperclip" icon-color='rgba(95, 73, 139, 1)' glow='true'
              class="mt-1 custom-file-icon" :disabled="isUploadActionBusy" @update:modelValue="onFilesUploadChange" />
          </VCol>

          <VCol cols="12">
            <div class="text-caption text-medium-emphasis">
              {{ getFileNameList().length ? `${getFileNameList().length} file(s) selected` : 'No files selected' }}
            </div>
          </VCol>

          <VCol v-if="showSelectedUploadFilesPanel" cols="12">
            <div class="files-upload-compare-grid"
              :class="{ 'files-upload-compare-grid--single': !showSelectionComparison }">
              <div v-if="showSelectionComparison" class="files-upload-compare-panel">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-2">
                    Selected item codes/filenames (A-Z)
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ sortedExpectedItems.length }} item(s)
                  </div>
                </div>
                <VDivider class="mb-2" />
                <VList density="compact" class="files-upload-compare-list py-0">
                  <VListItem v-for="item in sortedExpectedItems" :key="`expected-${item.id}`" class="px-0">
                    <template #prepend>
                      <VIcon icon="tabler-list-details" size="18" class="mr-2" color="rgba(95, 73, 139, 1)" />
                    </template>
                    <template #title>
                      <span class="files-upload-text-ellipsis">
                        {{ item.displayName }}
                      </span>
                    </template>
                  </VListItem>
                </VList>
              </div>

              <div class="files-upload-compare-panel">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="text-subtitle-2">
                    Files selected to upload (A-Z)
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ sortedSelectedUploadFileNames.length }} file(s)
                  </div>
                </div>
                <VDivider class="mb-2" />
                <VList density="compact" class="files-upload-compare-list py-0">
                  <VListItem v-for="(file, index) in sortedSelectedUploadFiles"
                    :key="`${getFileIdentity(file)}-${index}`" class="px-0">
                    <template #prepend>
                      <VIcon :icon="getFileIcon(file.name)" :color="getFileIconColor(file.name)" size="20"
                        class="mr-2" />
                    </template>
                    <template #title>
                      <span class="files-upload-text-ellipsis">
                        {{ file.name }}
                      </span>
                    </template>
                    <template #append>
                      <VBtn icon="mdi-close" size="x-small" variant="text" color="error"
                        :disabled="isUploadActionBusy" @click.stop="removeSelectedFiles([file])" />
                    </template>
                  </VListItem>
                </VList>
              </div>
            </div>
          </VCol>
        </VRow>

        <div v-if="hasFilesUploadResults" class="mt-2 files-upload-result-stack">
          <div
            v-for="group in filesUploadResultGroups"
            :key="group.title"
            :class="{ 'files-upload-result-panel': group.kind === 'filename-issues' }"
          >
            <template v-if="group.kind === 'filename-issues'">
              <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3">
                <div class="d-flex align-center">
                  <VAvatar color="warning" variant="tonal" size="34" class="mr-2">
                    <VIcon icon="tabler-alert-triangle" size="20" />
                  </VAvatar>
                  <div>
                    <div class="text-subtitle-2">
                      {{ group.title }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ filenameIssues.length }} files
                    </div>
                  </div>
                </div>
              </div>
              <VDivider class="mb-2" />
              <VList density="compact" class="files-upload-result-list py-0">
                <VListItem v-for="issue in filenameIssues" :key="issue.id" class="px-0 files-upload-filename-issue">
                  <template #prepend>
                    <VCheckboxBtn
                      :model-value="selectedFilenameIssueIds.includes(issue.id)"
                      :disabled="isUploadActionBusy"
                      class="mr-2"
                      :aria-label="`Select ${issue.originalName}`"
                      @update:model-value="toggleFilenameIssue(issue.id, $event)"
                      @click.stop
                    />
                    <VIcon :icon="getFileIcon(issue.originalName)" :color="getFileIconColor(issue.originalName)"
                      size="24" class="mr-2" />
                  </template>
                  <template #title>
                    <span class="files-upload-text-ellipsis">
                      {{ issue.originalName }}
                    </span>
                  </template>
                  <template #subtitle>
                    <div class="files-upload-filename-issue-body">
                      <div class="text-caption text-medium-emphasis files-upload-text-multiline">
                        {{ issue.message }}
                      </div>
                    </div>
                  </template>
                  <template #append>
                    <VBtn
                      color="primary"
                      variant="outlined"
                      size="small"
                      :disabled="!folder || isUploadActionBusy"
                      @click.stop="openReuploadDialog(issue)"
                    >
                      Re-upload
                    </VBtn>
                  </template>
                </VListItem>
              </VList>
              <VDivider class="mt-2 mb-1" />
              <div class="files-upload-filename-issue-footer">
                <VCheckboxBtn
                  :model-value="areAllFilenameIssuesSelected"
                  :indeterminate="isFilenameIssueSelectionIndeterminate"
                  :disabled="isUploadActionBusy"
                  label="Select all files"
                  aria-label="Select all files needing filename review"
                  @update:model-value="toggleAllFilenameIssues"
                />
                <VBtn
                  color="warning"
                  variant="outlined"
                  size="small"
                  class="ml-auto"
                  :loading="isUploadingAnyway"
                  :disabled="!folder || !selectedFilenameIssues.length || isUploadActionBusy"
                  @click="uploadSelectedFilenameIssuesAnyway"
                >
                  Upload anyway
                </VBtn>
              </div>

            </template>

            <template v-else>
            <div class="files-upload-result-panel">
              <div class="d-flex align-center mb-3">
                <VAvatar :color="group.color" variant="tonal" size="34" class="mr-2">
                  <VIcon :icon="group.icon" size="20" />
                </VAvatar>
                <div>
                  <div class="text-subtitle-2 d-flex align-center gap-1">
                    <span>{{ group.title }}</span>
                    <VMenu v-if="group.infoMessage" location="top center" open-on-hover open-delay="50"
                      close-delay="50">
                      <template #activator="{ props: infoMenuProps }">
                        <VIcon v-bind="infoMenuProps" icon="tabler-info-circle" size="17" color="rgba(95, 73, 139, 1)"
                          class="cursor-pointer" />
                      </template>
                      <VCard max-width="320" class="pa-3 text-caption files-upload-info-tooltip">
                        {{ group.infoMessage }}
                      </VCard>
                    </VMenu>
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ group.files.length }} {{ group.countLabel || 'files' }}
                  </div>
                </div>
              </div>
              <VDivider class="mb-2" />
              <VList v-if="group.title !== 'Files uploaded successfully'"
                density="compact" class="files-upload-result-list py-0">
                <ProjectFilesItemTooltip v-for="file in group.files" :key="file.key || `${group.title}-${file.name}`"
                  :content="file.message ? `${file.name}\n${file.message}` : file.name" v-slot="{ triggerProps }">
                  <VListItem v-bind="triggerProps" :href="file.link" :target="file.link ? '_blank' : undefined"
                    :rel="file.link ? 'noopener noreferrer' : undefined" class="px-0 files-upload-item"
                    :class="{ 'files-upload-item--link': !!file.link }">
                    <template #title>
                      <span class="files-upload-text-ellipsis">
                        {{ file.name }}
                      </span>
                    </template>

                    <template v-if="file.message" #subtitle>
                      <span class="files-upload-text-ellipsis"
                        :class="{ 'files-upload-text-multiline': file.multilineMessage }">
                        {{ file.message }}
                      </span>
                    </template>

                    <template #prepend>
                      <VIcon :icon="getFileIcon(file.name)" :color="getFileIconColor(file.name)" size="24"
                        class="mr-2" />
                    </template>
                  </VListItem>
                </ProjectFilesItemTooltip>
              </VList>
            </div>
            </template>
          </div>
        </div>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn variant="text" :disabled="isUploadActionBusy" @click="closeDialog">
          Close
        </VBtn>
        <VBtn color="primary" :loading="isUploading" :disabled="!isReadyToUpload || isUploadActionBusy"
          @click="uploadFiles">
          Upload
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <GlobalSnackbar
    v-for="(snackbar, index) in uploadSnackbars"
    :key="snackbar.id"
    :model-value="snackbar.show"
    :message="snackbar"
    :content-props="index ? { style: { top: `${index * 88}px` } } : undefined"
    :z-index="2501"
    @update:model-value="value => snackbar.show = value"
  />

  <ProjectFilesSelectProjectDialog
    v-model="showWaitingRoomProjectDialog"
    v-model:project="selectedWaitingRoomCampaign"
    :projects="waitingRoomCampaignOptions"
    confirm-text="Upload"
    @confirm="confirmWaitingRoomProject"
  />

  <ProjectFilesReuploadDialog
    :model-value="showReuploadDialog"
    :selected-files="reuploadSelectedFiles"
    :original-filename="reuploadIssue?.originalName || ''"
    :loading="isReuploading"
    :error-message="reuploadErrorMessage"
    @update:model-value="onReuploadDialogModelValueUpdate"
    @update:selected-files="reuploadSelectedFiles = $event"
    @confirm="reuploadFilenameIssue"
  />
</template>

<style scoped lang="scss">
:deep(.files-upload-resizable-dialog .v-overlay__content) {
  max-width: 95vw;
  max-height: 95vh;
}

.files-upload-dialog-card {
  position: relative;
  overflow: hidden;
}

.files-upload-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.files-upload-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 132px;
  border: 1px dashed rgba(95, 73, 139, 0.6);
  border-radius: 8px;
  background: rgba(95, 73, 139, 0.06);
  color: rgba(95, 73, 139, 0.72);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.files-upload-dropzone--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
}

.files-upload-result-panel {
  height: 100%;
  padding: 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.files-upload-result-stack {
  display: grid;
  gap: 12px;
}

.files-upload-result-list {
  background: transparent;
}

.files-upload-filename-issue {
  align-items: flex-start;
}

.files-upload-filename-issue-body {
  display: grid;
  gap: 8px;
  padding-block: 4px 8px;
}

.files-upload-filename-issue-footer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.files-upload-compare-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.files-upload-compare-grid--single {
  grid-template-columns: minmax(0, 1fr);
}

.files-upload-compare-panel {
  padding: 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 1);
}

.files-upload-compare-list {
  max-height: 220px;
  overflow: auto;
  background: transparent;
}

.files-upload-campaign-select-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.files-upload-help-btn {
  align-self: center;
}

.files-upload-info-tooltip {
  white-space: pre-line;
}

@media (max-width: 959px) {
  .files-upload-compare-grid {
    grid-template-columns: minmax(0, 1fr);
  }

}

.files-upload-text-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.files-upload-text-multiline {
  white-space: pre-line;
}

.files-upload-item {
  cursor: help;
}

.files-upload-item--link {
  cursor: pointer;
}

.custom-file-icon :deep(.v-input__prepend > .v-icon) {
  color: rgba(95, 73, 139, 1);
  opacity: 1;
  /* Vuetify often sets default icons to a lower opacity */
}

.movable-upload-dialog:hover {
  background-color: rgba(95, 73, 139, 0.12) !important;
}
</style>
